
import React from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import Markdown from "markdown-to-jsx";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coldarkDark } from "react-syntax-highlighter/dist/esm/styles/prism";

interface ResultContentProps {
  output: any;
  isJson: boolean;
  isMarkdown: boolean;
}

const CodeBlock = ({ className, children }: { className?: string; children: string }) => {
  const language = className ? className.replace(/language-/, "") : "text";
  return (
    <SyntaxHighlighter language={language} style={coldarkDark}>
      {children}
    </SyntaxHighlighter>
  );
};

export function ResultContent({ output, isJson, isMarkdown }: ResultContentProps) {
  if (!output) {
    return (
      <ScrollArea className="h-[400px]">
        <div className="p-4">
          <p className="text-muted-foreground">No output available.</p>
        </div>
      </ScrollArea>
    );
  }

  if (isJson) {
    return (
      <ScrollArea className="h-[400px]">
        <pre className="p-4 rounded-md bg-gray-50 dark:bg-gray-900 text-sm font-mono">
          {JSON.stringify(output, null, 2)}
        </pre>
      </ScrollArea>
    );
  }

  if (isMarkdown) {
    return (
      <ScrollArea className="h-[400px]">
        <div className="prose prose-gray dark:prose-invert max-w-none p-4">
          <Markdown
            options={{
              overrides: {
                code: {
                  component: CodeBlock
                },
                pre: {
                  component: ({ children, ...props }) => <div {...props}>{children}</div>
                }
              }
            }}
          >
            {output}
          </Markdown>
        </div>
      </ScrollArea>
    );
  }

  // Plain text
  return (
    <ScrollArea className="h-[400px]">
      <div className="p-4">
        <p className="whitespace-pre-wrap">{String(output)}</p>
      </div>
    </ScrollArea>
  );
}
